import React, { useState, useRef, useEffect } from "react";
import { API_URLS } from "../../apiUrl";
import PropertyTypeDropdown from "../customHooks/propertyTypeDropdown";
import axiosInstance from "../../components/axiosInstance";

type FormData = {
  type_description_name: string;
  property_type_id: string;
  property_type_name: string;
};

const AddPropertyTypeDescription: React.FC = () => {

  const [handleDropdownToggle, isDropdownOpen, propertyTypes, loading, setIsDropdownOpen] = PropertyTypeDropdown()

  const [formData, setFormData] = useState<FormData>({
    type_description_name: "",
    property_type_id: "",
    property_type_name: ""
  });

  const [errors, setErrors] = useState<any>({});
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [dataExistMessage, setDataExistMessage] = useState<string>("");

  const formRef = useRef<HTMLFormElement>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement> | any
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const resetForm = () => {
    setFormData({
      type_description_name: "",
      property_type_id: "",
      property_type_name: ""
    });
    setErrors({
      description_name: "",
    });
  };

  const validateForm = (): boolean => {
    const newErrors: any = {};
    if (!formData.type_description_name.trim()) {
      newErrors.type_description_name = "Property Type Description name is required";
    } else if (!/^[A-Za-z0-9\s]+$/.test(formData.type_description_name)) {
      newErrors.type_description_name = "Property Type Description name must contain only letters, numbers, and spaces";
    }

    if (!formData.property_type_id.trim()) newErrors.property_type_id = "Property Type is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      try {
        const { property_type_id, type_description_name } = formData;
        const response = await axiosInstance.post(`${API_URLS.addpropertyTypeDescription}`, {
          property_type_id,
          type_description_name,
        });

        if (response.data.data.message) {
          resetForm();
          setDataExistMessage(`${response.data.data.message}`);
          setTimeout(() => {
            setDataExistMessage("");
          }, 5000);
        } else {
          resetForm();
          setSuccessMessage(`${response.data.message}`);
          setTimeout(() => {
            setSuccessMessage("");
          }, 5000);
        }

        setErrors({});
      } catch (error) {
        setSuccessMessage("Property Type Description Add failed.");
        setTimeout(() => {
          setSuccessMessage("");
        }, 5000);
      }
    }
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (formRef.current && !formRef.current.contains(e.target as Node)) {
      setErrors({});
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handlePropertyTypeSelect = (selectedValue: string) => {
    const selectedProperty = propertyTypes.find(
      (property: any) => property.type_primary_id === parseInt(selectedValue)
    );

    setFormData((prevState) => ({
      ...prevState,
      property_type_id: selectedValue,
      property_type_name: selectedProperty ? selectedProperty.type_name : "",
    }));

    setIsDropdownOpen(false);
  };

  const dropdownRef = useRef<any>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  return (
    <div className="max-w-full mx-auto">
      <div className="flex flex-col gap-5">
        <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Add Property Type Description</h1>
        <div className="p-6 bg-white rounded-lg shadow-lg dark:bg-gray-800">
          <form ref={formRef} onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label
                  htmlFor="property_type_id"
                  className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                >
                  Property Type <span className="text-red-600">*</span>
                </label>
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={handleDropdownToggle}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5 pr-3 text-left flex items-center"
                    type="button"
                  >
                    {formData.property_type_name || "Select Property Type"}
                    <i className="ri-arrow-down-s-line ri-lg ml-auto"></i>
                  </button>

                  {isDropdownOpen && (
                    <select
                      id="property_type_id"
                      name="property_type_id"
                      value={formData.property_type_id}
                      onChange={(e) => handlePropertyTypeSelect(e.target.value)}
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 absolute top-full z-[2]"
                      size={10}
                    >
                      <option value="" disabled>Select Property Type</option>
                      {propertyTypes.map((property: any) => (
                        <option key={property.type_primary_id} value={property.type_primary_id}>
                          {property.type_name}
                        </option>
                      ))}
                      {loading && <option>Loading...</option>}
                    </select>
                  )}
                </div>
                {errors.property_type_id && (
                  <p className="text-sm text-red-600 mt-1">{errors.property_type_id}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="type_description_name"
                  className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                >
                  Description Name <span className="text-red-600">*</span>
                </label>
                <div className="flex">
                  <input
                    type="text"
                    id="type_description_name"
                    name="type_description_name"
                    value={formData.type_description_name}
                    onChange={handleChange}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  />
                </div>
                {errors.type_description_name && (
                  <p className="text-sm text-red-600 mt-1">{errors.type_description_name}</p>
                )}
              </div>

              <div className="lg:col-span-2 sm:col-span-2">
                <button
                  type="submit"
                  className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                >
                  Submit
                </button>
              </div>
            </div>
          </form>

          {successMessage && (
            <div className="mt-4 p-4 bg-green-600 text-white rounded-lg flex justify-between items-center">
              <span>{successMessage}</span>
              <button
                onClick={() => setSuccessMessage('')}
                className="ml-4 text-white font-bold text-lg focus:outline-none"
              >
                &times;
              </button>
            </div>
          )}

          {dataExistMessage && (
            <div className="mt-4 p-4 bg-yellow-600 text-white rounded-lg flex justify-between items-center">
              <span>{dataExistMessage}</span>
              <button
                onClick={() => setDataExistMessage('')}
                className="ml-4 text-white font-bold text-lg focus:outline-none"
              >
                &times;
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddPropertyTypeDescription;